import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSignatures,
  createSignature,
  updateSignature,
} from '../../lib/inboxSignaturesApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

export default function InboxSignatureEditorPage() {
  const navigate = useNavigate();
  const { signatureId } = useParams<{ signatureId?: string }>();
  const isEdit = !!signatureId;
  const qc = useQueryClient();

  const { data: signatures = [], isLoading: loadingExisting, isError: existingFailed, refetch } = useQuery({
    queryKey: ['inbox-signatures'],
    queryFn: fetchSignatures,
    enabled: isEdit,
  });

  const existing = signatures.find((s) => s.id === signatureId);

  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [isDefaultNew, setIsDefaultNew] = useState(false);
  const [isDefaultReply, setIsDefaultReply] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setBody(existing.body);
      setIsDefaultNew(existing.isDefaultNew);
      setIsDefaultReply(existing.isDefaultReply);
    }
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { name, body, isDefaultNew, isDefaultReply };
      if (isEdit && signatureId) return updateSignature(signatureId, payload);
      return createSignature(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-signatures'] });
      navigate('/barrister/inbox/settings/signatures');
    },
    onError: () => setError('Failed to save signature.'),
  });

  function handleSave() {
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!body.trim()) { setError('Signature body is required.'); return; }
    setError('');
    saveMutation.mutate();
  }

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--border-strong)', borderRadius: 6,
    padding: '10px 12px', fontSize: 16, color: 'var(--text-primary)',
    fontFamily: "'IBM Plex Sans', sans-serif",
    outline: 'none', width: '100%', boxSizing: 'border-box' as const,
  };

  return (
    <div style={{
      maxWidth: 680, margin: '0 auto',
      padding: 'clamp(20px, 5vw, 40px) clamp(14px, 4vw, 24px)',
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      {/* Was an unlabelled arrow wired to a fixed destination. These pages are opened from the
          inbox, from the Email settings hub and from Settings, so a fixed target sent people
          somewhere they had never been. Back means back — same control as every other page. */}
      <BackButton onClick={() => navigate(-1)} />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          {isEdit ? 'Edit signature' : 'New signature'}
        </h1>
      </div>

      {/* Editing, but the signature could not be fetched: the form would otherwise sit there
          blank under the heading "Edit signature", and whatever was typed into it would be saved
          over the signature actually stored. An empty form is not an empty signature. */}
      {isEdit && existingFailed ? (
        <QueryErrorState what="this signature" onRetry={() => void refetch()} />
      ) : isEdit && loadingExisting ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
              Signature name
            </label>
            <input
              style={inputStyle}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Professional, Junior Barrister"
            />
          </div>

          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
              Signature content
            </label>
            <textarea
              style={{
                ...inputStyle,
                minHeight: 200, resize: 'vertical', lineHeight: 1.7,
              }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`James Thornton KC\nSmith & Co Chambers\n1 Gray's Inn Square, London WC1R 5AA\nt: +44 20 7123 4567`}
            />
          </div>

          {/* Preview */}
          {body && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 8 }}>
                Preview
              </div>
              <div style={{
                border: '1px solid var(--border-default)', borderRadius: 6,
                padding: '14px 18px', background: '#fafafa',
                fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}>
                {body}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isDefaultNew}
                onChange={(e) => setIsDefaultNew(e.target.checked)}
                style={{ width: 16, height: 16, marginTop: 2 }}
              />
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                  Use as default for new messages
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Automatically inserted when composing a new email
                </div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isDefaultReply}
                onChange={(e) => setIsDefaultReply(e.target.checked)}
                style={{ width: 16, height: 16, marginTop: 2 }}
              />
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                  Use as default for replies
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Automatically inserted when replying to or forwarding a message
                </div>
              </div>
            </label>
          </div>

          <div style={{
            fontSize: 12, color: 'var(--text-muted)',
            background: 'var(--surface)', border: '1px solid var(--border-default)',
            borderRadius: 6, padding: '10px 14px',
          }}>
            Setting a new default will automatically clear the previous default signature of the same type.
          </div>

          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 6, padding: '8px 14px', fontSize: 13, color: '#dc2626',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
            <button
              onClick={handleSave} className="btn-mobile-full"
              disabled={saveMutation.isPending}
              style={{
                padding: '11px 22px', borderRadius: 6, flex: 1, minWidth: 150,
                background: '#1e3a5f', color: 'white',
                border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
                opacity: saveMutation.isPending ? 0.6 : 1,
              }}
            >
              {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create signature'}
            </button>
            <button
              onClick={() => navigate('/barrister/inbox/settings/signatures')} className="btn-mobile-full"
              style={{
                padding: '11px 22px', borderRadius: 6, flex: 1, minWidth: 150,
                background: 'var(--surface-card)', color: 'var(--text-primary)',
                border: '1px solid var(--border-default)', cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
